import type { MediaKind } from '../mediaViewer/mediaFileTypes';
import { getEditorOpenMode, type EditorOpenMode } from './editorOpenPreference';
import { getViewerOpenMode, type ViewerOpenMode } from './viewerOpenPreference';
import { buildMinimalBrowserTabHref, editorHrefForKind, workspaceHref } from './editorRoutes';
import {
  buildEditorTabId,
  defaultTabTitle,
  surfaceToEditorTabKind,
  type EditorAccessMode,
  type EditorTabKind
} from './editorWorkspaceIds';
import { useEditorWorkspaceStore } from './useEditorWorkspaceStore';
import { resolveOpenSurface, type FileOpenSurface } from '../fileBrowser/fileTypeRegistry';
import type { DocumentEditorKind } from '../documentEditor/documentFileTypes';
import { documentKindToTabKind } from './editorRoutes';
import { getMediaKind } from '../mediaViewer/mediaFileTypes';
import { mediaViewerHref } from '../mediaViewer/mediaPreviewUrls';
import { pdfViewerHref } from '../pdfViewer/pdfPreviewUrls';

export type NavigateFn = (to: string) => void;

function openViewerBrowserTab(href: string): void {
  window.open(buildMinimalBrowserTabHref(href), '_blank', 'noopener,noreferrer');
}

export function openEditorBySurface(options: {
  surface: Extract<FileOpenSurface, 'monaco' | 'code' | 'markdown' | 'drawio'>;
  mountId: string;
  path: string;
  navigate?: NavigateFn;
  accessMode?: EditorAccessMode;
  preferredMode?: EditorOpenMode;
}): void {
  const kind = surfaceToEditorTabKind(options.surface);
  if (!kind) return;
  openEditorSession({
    kind,
    mountId: options.mountId,
    path: options.path,
    navigate: options.navigate,
    accessMode: options.accessMode,
    preferredMode: options.preferredMode
  });
}

export function openEditorSession(options: {
  kind: EditorTabKind;
  mountId: string;
  path: string;
  title?: string;
  mediaKind?: MediaKind;
  navigate?: NavigateFn;
  preferredMode?: EditorOpenMode;
  accessMode?: EditorAccessMode;
}): void {
  const accessMode = options.accessMode ?? 'edit';
  const href = editorHrefForKind(
    options.kind,
    options.mountId,
    options.path,
    options.mediaKind,
    accessMode
  );
  const mode = options.preferredMode ?? getEditorOpenMode();

  if (mode === 'browserTab') {
    window.open(buildMinimalBrowserTabHref(href), '_blank', 'noopener,noreferrer');
    return;
  }

  const tabId = useEditorWorkspaceStore.getState().openTab({
    kind: options.kind,
    mountId: options.mountId,
    path: options.path,
    title: options.title ?? defaultTabTitle(options.path),
    mediaKind: options.mediaKind,
    accessMode
  });

  const target = workspaceHref(tabId);
  if (options.navigate) {
    options.navigate(target);
  } else {
    window.location.assign(target);
  }
}

export function openDocumentEditorSession(options: {
  kind: DocumentEditorKind;
  mountId: string;
  path: string;
  navigate?: NavigateFn;
  accessMode?: EditorAccessMode;
  preferredMode?: EditorOpenMode;
}): void {
  openEditorSession({
    kind: documentKindToTabKind(options.kind),
    mountId: options.mountId,
    path: options.path,
    navigate: options.navigate,
    accessMode: options.accessMode,
    preferredMode: options.preferredMode
  });
}

/** Open a registered storage file in a Workspace tab. Images/videos stay on their media surface. */
export function openRegisteredStorageFile(options: {
  fileName: string;
  mountId: string;
  path: string;
  navigate?: NavigateFn;
  accessMode?: EditorAccessMode;
}): boolean {
  const surface = resolveOpenSurface(options.fileName);
  const accessMode = options.accessMode ?? 'edit';
  switch (surface) {
    case 'pdf':
      openPdfViewerSession({
        mountId: options.mountId,
        path: options.path,
        navigate: options.navigate,
        accessMode,
        preferredMode: 'inAppTab'
      });
      return true;
    case 'spreadsheet':
    case 'richedit':
      openDocumentEditorSession({
        kind: surface,
        mountId: options.mountId,
        path: options.path,
        navigate: options.navigate,
        accessMode,
        preferredMode: 'inAppTab'
      });
      return true;
    case 'monaco':
    case 'code':
    case 'markdown':
    case 'drawio':
      openEditorBySurface({
        surface,
        mountId: options.mountId,
        path: options.path,
        navigate: options.navigate,
        accessMode,
        preferredMode: 'inAppTab'
      });
      return true;
    case 'media': {
      const kind = getMediaKind(options.fileName);
      if (!kind) return false;
      openMediaViewerSession({
        kind,
        mountId: options.mountId,
        path: options.path,
        navigate: options.navigate,
        preferredMode: 'inAppTab'
      });
      return true;
    }
    default:
      return false;
  }
}

export function openPdfViewerSession(options: {
  mountId: string;
  path: string;
  navigate?: NavigateFn;
  /** Force a mode (e.g. toolbar "Open PDF viewer" always uses inAppTab). */
  preferredMode?: ViewerOpenMode;
  accessMode?: EditorAccessMode;
  setPreview?: (state: { open: boolean; mountId: string; relativePath: string }) => void;
}): void {
  const mode = options.preferredMode ?? getViewerOpenMode();
  const href = pdfViewerHref(options.mountId, options.path);

  if (mode === 'browserTab') {
    openViewerBrowserTab(href);
    return;
  }

  if (mode === 'inAppTab') {
    openEditorSession({
      kind: 'pdf',
      mountId: options.mountId,
      path: options.path,
      preferredMode: 'inAppTab',
      accessMode: options.accessMode,
      navigate: options.navigate
    });
    return;
  }

  if (options.setPreview) {
    options.setPreview({
      open: true,
      mountId: options.mountId,
      relativePath: options.path
    });
    return;
  }

  // Avoid silent no-op when popup mode has no preview host (toolbar Open viewer).
  openViewerBrowserTab(href);
}

export function openMediaViewerSession(options: {
  kind: MediaKind;
  mountId: string;
  path: string;
  navigate?: NavigateFn;
  /** Force a mode (e.g. toolbar "Open viewer" always uses inAppTab). */
  preferredMode?: ViewerOpenMode;
  setPreview?: (state: {
    open: boolean;
    kind: MediaKind;
    mountId: string;
    relativePath: string;
  }) => void;
}): void {
  const mode = options.preferredMode ?? getViewerOpenMode();
  const href = mediaViewerHref(options.kind, options.mountId, options.path);

  if (mode === 'browserTab') {
    openViewerBrowserTab(href);
    return;
  }

  if (mode === 'inAppTab') {
    openEditorSession({
      kind: 'media',
      mountId: options.mountId,
      path: options.path,
      mediaKind: options.kind,
      preferredMode: 'inAppTab',
      navigate: options.navigate
    });
    return;
  }

  if (options.setPreview) {
    options.setPreview({
      open: true,
      kind: options.kind,
      mountId: options.mountId,
      relativePath: options.path
    });
    return;
  }

  // Avoid silent no-op when popup mode has no preview host (toolbar Open viewer).
  openViewerBrowserTab(href);
}

export function openMediaPreviewSession(options: {
  kind: MediaKind;
  mountId: string;
  path: string;
  navigate?: NavigateFn;
  setPreview?: (state: {
    open: boolean;
    kind: MediaKind;
    mountId: string;
    relativePath: string;
  }) => void;
}): void {
  const mode = getViewerOpenMode();
  if (mode === 'inAppPopup') {
    options.setPreview?.({
      open: true,
      kind: options.kind,
      mountId: options.mountId,
      relativePath: options.path
    });
    return;
  }

  openMediaViewerSession(options);
}

export function openPdfPreviewSession(options: {
  mountId: string;
  path: string;
  navigate?: NavigateFn;
  setPreview?: (state: { open: boolean; mountId: string; relativePath: string }) => void;
}): void {
  if (options.setPreview) {
    options.setPreview({
      open: true,
      mountId: options.mountId,
      relativePath: options.path
    });
    return;
  }

  openPdfViewerSession({ ...options, preferredMode: 'inAppTab' });
}

export function tabIdFromSurface(
  surface: FileOpenSurface,
  mountId: string,
  path: string
): string | null {
  const kind = surfaceToEditorTabKind(surface);
  if (!kind) return null;
  return buildEditorTabId(kind, mountId, path);
}
